import { Alert, Box, Stack, Text } from '@mantine/core';
import { useSearchParams } from 'react-router-dom';
import { FormPageLayout, PlatformLoader } from '@flexinet/ui-components';
import { useUserAccount } from '../../../../hooks/useUserAccount';
import { PaymentMethodSetupForm, StripeProvider } from '../../stripe-integration';
import { PayPalProvider, PayPalPaymentMethodSetupForm } from '../../paypal-integration';
import { useEffect, useState } from 'react';
import { initializePaymentProfileSetup } from '../services/PaymentProfilesService';
import { billingClient } from '../../billing-api-integration/client';
import { GraphQLQuery } from '@aws-amplify/api';

export const AddPage = () => {
    const [searchParams] = useSearchParams();
    const { userAccount } = useUserAccount();
    const [clientSecret, setClientSecret] = useState<string>('');
    const [providerCustomerId, setProviderCustomerId] = useState<string>('');
    const [sessionId, setSessionId] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const providerKey = searchParams.get('providerKey') || 'stripe';
    const providerId = searchParams.get('providerId') || '';

    useEffect(() => {
        const customerId = userAccount?.customerId;
        if (!customerId) return;

        if (!providerId) {
            setError('Missing billing provider. Please go back and select a provider.');
            setLoading(false);
            return;
        }

        const initializeSetup = async () => {
            try {
                // Step 1: Ensure customer is registered with the billing provider (idempotent)
                try {
                    type EnableProviderResponse = {
                        enableBillingProviderForCurrentCustomer: {
                            id: string;
                            providerEntityId: string;
                            syncStatus: string;
                        };
                    };
                    const enableResponse = await billingClient.graphql<GraphQLQuery<EnableProviderResponse>>({
                        query: `mutation enableBillingProviderForCurrentCustomer($billingProviderId: ID!) {
                            enableBillingProviderForCurrentCustomer(billingProviderId: $billingProviderId) { id providerEntityId syncStatus }
                        }`,
                        variables: { billingProviderId: providerId }
                    });
                    console.log('Enable provider response:', JSON.stringify(enableResponse));

                    if (enableResponse.errors?.length) {
                        console.error('Enable provider errors:', enableResponse.errors);
                        throw new Error(enableResponse.errors[0].message);
                    }
                } catch (enableError: unknown) {
                    // Log the full error shape to understand what Amplify throws
                    console.error('Enable provider threw:', JSON.stringify(enableError, Object.getOwnPropertyNames(enableError as object)));

                    // Extract the actual message from whatever shape Amplify gives us
                    const err = enableError as { errors?: { message?: string }[]; message?: string } | undefined;
                    const msg = err?.errors?.[0]?.message || err?.message || 'Failed to enable billing provider';
                    throw new Error(msg);
                }

                // Step 2: Initialize payment profile setup
                if (providerKey === 'stripe') {
                    const setup = await initializePaymentProfileSetup(providerId);
                    if (setup) {
                        setClientSecret(setup.clientToken);
                        setProviderCustomerId(setup.providerCustomerId);
                        setSessionId(setup.sessionId);
                    }
                }
            } catch (e: unknown) {
                console.error('Payment setup failed:', e);
                const err = e as { message?: string } | undefined;
                setError(err?.message || 'Failed to initialize payment setup. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        initializeSetup();
    }, [userAccount?.customerId, providerKey, providerId]);

    const handleSuccess = () => {
        const returnTo = searchParams.get('returnTo');
        if (returnTo) {
            window.location.href = returnTo;
        } else {
            window.location.href = '/billing/providers';
        }
    };

    const handleCancel = () => {
        const returnTo = searchParams.get('returnTo');
        if (returnTo) {
            window.location.href = returnTo;
        } else {
            window.location.href = '/billing/providers';
        }
    };

    if (loading) {
        return <PlatformLoader message="Setting up payment..." />;
    }

    return (
        <FormPageLayout title="Add Payment Method" pageNavigation={<Box />}>
            <Stack gap="lg" p="md" maw={600} mx="auto">
                <Text size="sm" c="dimmed">
                    Add a payment method to complete your subscription
                </Text>

                {error && (
                    <Alert color="red" title="Error">
                        {error}
                    </Alert>
                )}

                {providerKey === 'stripe' && clientSecret && providerCustomerId && (
                    <StripeProvider clientSecret={clientSecret}>
                        <PaymentMethodSetupForm
                            customerId={providerCustomerId}
                            billingProviderId={providerId}
                            sessionId={sessionId}
                            onSuccess={handleSuccess}
                            onCancel={handleCancel}
                        />
                    </StripeProvider>
                )}

                {providerKey === 'paypal' && userAccount?.customerId && (
                    <PayPalProvider intent="authorize">
                        <PayPalPaymentMethodSetupForm
                            customerId={userAccount.customerId}
                            onSuccess={handleSuccess}
                            onCancel={handleCancel}
                        />
                    </PayPalProvider>
                )}
            </Stack>
        </FormPageLayout>
    );
};
